// User store for managing users data (for super admin functionality)
// This provides localStorage persistence until database is connected

export interface StoredUser {
  id: string
  email: string
  name: string
  avatarUrl?: string
  role: 'owner' | 'admin' | 'manager' | 'viewer'
  tier: 'free' | 'pro' | 'business'
  organizationName: string
  createdAt: string // ISO date string
  suspended: boolean
  suspendedAt?: string // ISO date string
  suspendedBy?: string // super admin email
  suspendedReason?: string
  // Stats
  dropsCount: number
  photosCount: number
  contributorsCount: number
  lastLoginAt?: string // ISO date string
}

const USERS_STORAGE_KEY = 'photodrop_users'

// Default sample users for demo/testing purposes
const defaultUsers: StoredUser[] = [
  {
    id: 'user-1',
    email: 'john.doe@example.com',
    name: 'John Doe',
    role: 'owner',
    tier: 'pro',
    organizationName: "John's Photography",
    createdAt: '2025-06-15T10:00:00Z',
    suspended: false,
    dropsCount: 12,
    photosCount: 456,
    contributorsCount: 34,
    lastLoginAt: '2026-01-09T08:30:00Z',
  },
  {
    id: 'user-2',
    email: 'jane.smith@example.com',
    name: 'Jane Smith',
    role: 'owner',
    tier: 'business',
    organizationName: 'Smith Events Co.',
    createdAt: '2025-03-22T14:30:00Z',
    suspended: false,
    dropsCount: 45,
    photosCount: 2340,
    contributorsCount: 156,
    lastLoginAt: '2026-01-08T16:45:00Z',
  },
  {
    id: 'user-3',
    email: 'bob.wilson@example.com',
    name: 'Bob Wilson',
    role: 'owner',
    tier: 'free',
    organizationName: "Bob's Wedding Photos",
    createdAt: '2025-11-01T09:15:00Z',
    suspended: false,
    dropsCount: 2,
    photosCount: 89,
    contributorsCount: 12,
    lastLoginAt: '2026-01-07T11:20:00Z',
  },
  {
    id: 'user-4',
    email: 'alice.johnson@example.com',
    name: 'Alice Johnson',
    role: 'owner',
    tier: 'pro',
    organizationName: 'Alice Events',
    createdAt: '2025-08-10T16:00:00Z',
    suspended: false,
    dropsCount: 8,
    photosCount: 234,
    contributorsCount: 28,
    lastLoginAt: '2026-01-06T14:00:00Z',
  },
  {
    id: 'user-5',
    email: 'suspicious.user@spam.com',
    name: 'Suspicious User',
    role: 'owner',
    tier: 'free',
    organizationName: 'Spam Organization',
    createdAt: '2026-01-01T00:00:00Z',
    suspended: false,
    dropsCount: 0,
    photosCount: 0,
    contributorsCount: 0,
  },
]

// Initialize users store
function initializeStore(): StoredUser[] {
  const stored = localStorage.getItem(USERS_STORAGE_KEY)
  if (stored) {
    try {
      return JSON.parse(stored)
    } catch {
      // Invalid data, reset to defaults
    }
  }
  // Initialize with default users
  localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(defaultUsers))
  return defaultUsers
}

// Get all users
export function getUsers(): StoredUser[] {
  return initializeStore()
}

// Get a single user by ID
export function getUserById(userId: string): StoredUser | null {
  const users = getUsers()
  return users.find(u => u.id === userId) || null
}

// Get a single user by email
export function getUserByEmail(email: string): StoredUser | null {
  const users = getUsers()
  return users.find(u => u.email.toLowerCase() === email.toLowerCase()) || null
}

// Check if a user is suspended by email
export function isUserSuspended(email: string): boolean {
  const user = getUserByEmail(email)
  return user?.suspended === true
}

// Suspend a user
export function suspendUser(
  userId: string,
  suspendedBy: string,
  reason?: string
): StoredUser | null {
  const users = getUsers()
  const userIndex = users.findIndex(u => u.id === userId)

  if (userIndex === -1) return null

  const user = users[userIndex]
  const updatedUser: StoredUser = {
    ...user,
    suspended: true,
    suspendedAt: new Date().toISOString(),
    suspendedBy,
    suspendedReason: reason,
  }

  users[userIndex] = updatedUser
  localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(users))

  return updatedUser
}

// Unsuspend a user
export function unsuspendUser(userId: string): StoredUser | null {
  const users = getUsers()
  const userIndex = users.findIndex(u => u.id === userId)

  if (userIndex === -1) return null

  const user = users[userIndex]
  const updatedUser: StoredUser = {
    ...user,
    suspended: false,
    suspendedAt: undefined,
    suspendedBy: undefined,
    suspendedReason: undefined,
  }

  users[userIndex] = updatedUser
  localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(users))

  return updatedUser
}

// Add a new user to the store
export function addUser(user: StoredUser): StoredUser {
  const users = getUsers()

  // Check for duplicate email
  if (users.some(u => u.email.toLowerCase() === user.email.toLowerCase())) {
    throw new Error('User with this email already exists')
  }

  users.push(user)
  localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(users))

  return user
}

// Update user (for updating stats, etc.)
export function updateUser(userId: string, updates: Partial<StoredUser>): StoredUser | null {
  const users = getUsers()
  const userIndex = users.findIndex(u => u.id === userId)

  if (userIndex === -1) return null

  const updatedUser: StoredUser = {
    ...users[userIndex],
    ...updates,
  }

  users[userIndex] = updatedUser
  localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(users))

  return updatedUser
}

// Search users by name or email
export function searchUsers(query: string): StoredUser[] {
  const users = getUsers()
  const lowerQuery = query.toLowerCase()

  return users.filter(
    u =>
      u.name.toLowerCase().includes(lowerQuery) ||
      u.email.toLowerCase().includes(lowerQuery) ||
      u.organizationName.toLowerCase().includes(lowerQuery)
  )
}

// Get user statistics
export function getUserStats(): {
  total: number
  byTier: Record<string, number>
  suspended: number
  activeToday: number
} {
  const users = getUsers()
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const byTier: Record<string, number> = { free: 0, pro: 0, business: 0 }
  let suspended = 0
  let activeToday = 0

  for (const user of users) {
    byTier[user.tier] = (byTier[user.tier] || 0) + 1
    if (user.suspended) suspended++
    if (user.lastLoginAt) {
      const lastLogin = new Date(user.lastLoginAt)
      if (lastLogin >= today) activeToday++
    }
  }

  return {
    total: users.length,
    byTier,
    suspended,
    activeToday,
  }
}

// Get system-wide statistics (aggregated from all users)
export function getSystemStats(): {
  totalDrops: number
  totalPhotos: number
  totalContributors: number
  storageUsedGB: number
  storageUsedFormatted: string
} {
  const users = getUsers()

  let totalDrops = 0
  let totalPhotos = 0
  let totalContributors = 0

  for (const user of users) {
    totalDrops += user.dropsCount || 0
    totalPhotos += user.photosCount || 0
    totalContributors += user.contributorsCount || 0
  }

  // Estimate storage: ~2MB per photo on average
  const storageUsedBytes = totalPhotos * 2 * 1024 * 1024
  const storageUsedGB = storageUsedBytes / (1024 * 1024 * 1024)

  // Format storage size
  let storageUsedFormatted: string
  if (storageUsedGB >= 1) {
    storageUsedFormatted = `${storageUsedGB.toFixed(2)} GB`
  } else {
    const storageUsedMB = storageUsedBytes / (1024 * 1024)
    storageUsedFormatted = `${storageUsedMB.toFixed(0)} MB`
  }

  return {
    totalDrops,
    totalPhotos,
    totalContributors,
    storageUsedGB,
    storageUsedFormatted,
  }
}

// Clear all users (for testing)
export function clearUsers(): void {
  localStorage.removeItem(USERS_STORAGE_KEY)
}

// Reset to default users (for testing)
export function resetUsersToDefault(): void {
  localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(defaultUsers))
}
